/**
 * OAuth callback handler — receives the backend's post-OAuth redirect.
 *
 * Backend signs the user in via Google / Microsoft / Facebook, then
 * redirects to `${FRONTEND_URL}/auth/callback?accessToken=…&refreshToken=…&isNewUser=…`.
 * We:
 *   1. Pull the tokens off the query string.
 *   2. Persist them via `setTokens()` (mirrors the email/password flow).
 *   3. Trigger `refreshUser()` so AuthProvider hydrates the user.
 *   4. Strip the tokens from the URL (`history.replaceState`) so they
 *      don't leak via browser history.
 *   5. Navigate to the destination — `?next=` if present, else the
 *      member dashboard.
 *
 * On error (missing tokens or refresh failure), show a small "couldn't
 * complete sign-in" panel with a link back to /login.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { setTokens } from '@/services/api';

function sanitizeNext(next: string | null): string | null {
  if (!next) return null;
  if (!next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = searchParams.get('accessToken');
    const refreshToken = searchParams.get('refreshToken');
    const errorParam = searchParams.get('error');
    const next = sanitizeNext(searchParams.get('next')) ?? '/member/dashboard';

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }
    if (!accessToken || !refreshToken) {
      setError('No tokens returned by the OAuth provider.');
      return;
    }

    setTokens(accessToken, refreshToken);

    // Strip the tokens from the URL bar before navigating.
    window.history.replaceState({}, '', '/auth/callback');

    refreshUser()
      .then(() => navigate(next, { replace: true }))
      .catch((err: Error) => setError(err.message || 'Failed to load user after sign-in.'));
  }, [searchParams, navigate, refreshUser]);

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto card text-center">
          <h1 className="text-2xl font-display font-bold text-white mb-2">Sign-in failed</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/login" className="btn-primary">Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16 text-center text-white/60">
      Signing you in…
    </div>
  );
}

export default AuthCallbackPage;
